import { Component, Vue, Watch } from "vue-property-decorator";

import * as firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import { v4 as uuidv4 } from "uuid";

import { FacilitySettingsModel } from "@/models/FacilitySettings";

import { Countries, countries } from "@/consts/countries";
import { CURRENCIES } from "@/consts/currencies";
import { Resident } from "@/models/resident/Resident";
import { ResidentAccessesOnMyhub } from "@/enums/ResidentAccessesOnMyhub";
import { dateFormats, timeFormats } from "@/consts/DateTimeFormats";
import { getDominantColor } from "@/utils/upload-image";
import { familyNewsletterOptions } from "@/consts/newsletter";
import { Languages, languages } from "@/consts/languages";
import { batchUpdate } from "@/utils/batchUpdate";
import { API } from "@/api";
import StaffDirectory from "@/views/settings/staffDirectory/StaffDirectory.vue";
import moment from "moment";
import {StaffAccessLevels} from "@/enums/StaffAccessLevels";
import ConfirmDlg from '@/components/popup/confirm-dialog/ConfirmDlg.vue';

@Component({
  components: {
    StaffDirectory,
    ConfirmDlg
  }
})
export default class FacilitySettings extends Vue {
  countries: Countries[] = countries;
  languages: Languages[] = languages;
  timeZones = moment.tz.names();
  currencies = CURRENCIES;
  currentDate: number = new Date().getDate();
  search = "";
  staffHubLogoUploadStatus = 100;
  facilityLogoUploadStatus = 100;
  orgLogoUploadStatus = 100;

  appName = "";
  packageName = "";
  mediaAppSelectedIndex = -1;
  tvAppName = "";
  arecheckedesdisabled = true;
  tvPackageName = "";
  tvAppStatus = false;
  tvAppSelectedIndex = -1;
  isMediaAppUpdating = false
  isMediaAppDeleting = false
  isTvAppUpdating = false
  dialog = false;
  residentAccessesOnMyhub = ResidentAccessesOnMyhub;
  residentFeaturesChanged = {
    isServicesChanged: false,
    isCommunityChanged: false,
    isMediaChanged: false,
    isMaintenanceChanged: false,
    isNoticeboardChanged: false,
    isBudgetChanged: false,
    isCalendarChanged: false
  }
  familyFeaturesChanged = {
    isCareSummaryChanged: false,
    isCarePlanChanged: false,
    isDailyCareChanged: false,
    isDailyPlanChanged: false
  }
  dateFormats = dateFormats
  timeFormats = timeFormats
  loading = false;
  isChangedFacilityName = false;
  facilityCopy = null
  organisation: any = { isGroveMenuFeatureEnabled: false }
  isInvalidFacilityLogo = false
  isInvalidOrgLogo = false
  isInvalidStaffHubLogo = false

  get facility(): FacilitySettingsModel {
    return this.$store.state.facilityModule.selectedFacility;
  }

  get residents(): Resident[] {
    return this.$store.state.residentModule.residentsShortData;
  }

  get familyMembers() {
    return this.$store.state.residentModule.familyMembers;
  }

  get isUserOrgAdmin() {
    return this.$store.state.authModule.user.accessLevel === StaffAccessLevels.OrgAdmin;
  }

  async created(): Promise<void> {
    await this.$store.dispatch("getFacility");
    await this.$store.dispatch("getFamilyMembers");
    this.initWeeklyLifestyleEventsReportOption(this.facility.countryIso)
    this.facilityCopy = { ...this.facility }
    this.organisation = await API.organisations.getOrganisationById(this.facility.orgId);
  }

  initWeeklyLifestyleEventsReportOption(countryIso){
  // for 'US' country the report should be enabled by default
  this.facility.isEnableWeeklyLifestyleEventsReport = 
  (this.facility.isEnableWeeklyLifestyleEventsReport == null && countryIso === 'US')? true : this.facility.isEnableWeeklyLifestyleEventsReport
  }

  async updateFacility(): Promise<void> {
    this.loading = true
    this.facility.facilityId = this.facility.id;
    this.facility.isEnableWeeklyLifestyleEventsReport = this.facility.isEnableWeeklyLifestyleEventsReport || false;
    this.facility.isEnableStaffAppNotifications = this.facility.isEnableStaffAppNotifications || false;
    this.facility.isEnableCSVImportForResidents = this.facility.isEnableCSVImportForResidents || false;
    this.facility.isEnableManuallyAddDeleteResidents = this.facility.isEnableManuallyAddDeleteResidents || false;
    this.facility.isEnableResidentsCanInviteFamily = this.facility.isEnableResidentsCanInviteFamily || false; 
    this.facility.isEnableADT = this.facility.isEnableADT || false;

    if(this.facility.isEnableADT === true){
      this.facility.isEnableCSVImportForResidents = false;
      this.facility.isEnableManuallyAddDeleteResidents = false;
    }
    
    this.setFamilyNewsletter();
    this.updateFacilityName(); 

    await this.$store.dispatch("updateFacility", this.facility).then(async () => {
      await this.updateFamilyMembersCareSettings();

      this.updateResidentsAccessesOnMyhub().then(() => {
        this.$toast.open({ message: (this.$i18n.messages[this.$i18n.locale]['Settings.FacilitySettingsUpdated'] as string), type: "success" });
      }).catch(() => {
        this.$toast.open({ message: (this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.UnhandledError'] as string), type: "error" });
      }).finally(() => {
        this.loading = false
      })
    })

    if (this.facility.languageIso != this.facilityCopy.languageIso) {
      await this.$store.dispatch("translate", { locale: this.facility.languageIso });
      this.facilityCopy.languageIso = this.facility.languageIso
    }
  }

  updateFacilityName() {
    this.loading = true
    const facility = this.$store.state.authModule.user.facilities.find(x => x.id == this.facility.id);

    if (this.isChangedFacilityName) {
      facility.name = this.facility.name
      this.$store.commit("setCurrentFacilityData", this.facility);
      this.$store.dispatch("updateFacilityName", {
        name: this.facility.name,
        facilityId: this.facility.id,
        facilityOrgId: this.facility.orgId,
      }).then(() => {
        this.loading = false
      })
    }
  }

  setFamilyNewsletter() {
    if (!this.facility.familyNewsletterOptions && this.facility.isEnableFamilyNewsletter) {
      this.facility.familyNewsletterOptions = familyNewsletterOptions
    }
  }

  saveStaffHubLogo(event: any) {

    this.isInvalidStaffHubLogo = !['png', 'jpeg', 'jpg'].includes(this.getExtension(event.target.files[0].name).toLowerCase())
    
    if(this.isInvalidStaffHubLogo) return

    const ref = uuidv4();
    const storageRef = firebase
      .storage()
      .ref()
      .child(this.facility.id)
      .child("facility")
      .child("staffHubLogo")
      .child(ref)
      .put(event.target.files[0]);

    storageRef.on(
      `state_changed`,
      snapshot => {
        this.staffHubLogoUploadStatus =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      },
      error => {
        console.log(error.message);
      },
      () => {
        storageRef.snapshot.ref.getDownloadURL().then(async url => {
          console.log(url);
          this.facility.staffHubLogoUrl = url;
          this.facility.staffHubLogoColor = await getDominantColor(url)
          this.updateFacility();
        });
      }
    );
  }

  saveOrgLogo(event: any) {

    this.isInvalidOrgLogo = !['png', 'jpeg', 'jpg'].includes(this.getExtension(event.target.files[0].name).toLowerCase())

    if(this.isInvalidOrgLogo) return

    const ref = uuidv4();
    const storageRef = firebase
      .storage()
      .ref()
      .child(this.facility.id)
      .child("facility")
      .child("orgLogo")
      .child(ref)
      .put(event.target.files[0]);

    storageRef.on(
      `state_changed`,
      snapshot => {
        this.orgLogoUploadStatus =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      },
      error => {
        console.log(error.message);
      },
      () => {
        storageRef.snapshot.ref.getDownloadURL().then(async url => {
          console.log(url);
          this.facility.orgLogoUrl = url;
          this.facility.orgLogoColor = await getDominantColor(url)
          this.updateFacility();
        });
      }
    );
  }

  saveFacilityLogo(event: any) {

    this.isInvalidFacilityLogo = !['png', 'jpeg', 'jpg'].includes(this.getExtension(event.target.files[0].name).toLowerCase())

    if(this.isInvalidFacilityLogo) return

    const ref = uuidv4();
    const storageRef = firebase
      .storage()
      .ref()
      .child(this.facility.id)
      .child("facility")
      .child("facilityLogo")
      .child(ref)
      .put(event.target.files[0]);

    storageRef.on(
      `state_changed`,
      snapshot => {
        this.facilityLogoUploadStatus =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      },
      error => {
        console.log(error.message);
      },
      () => {
        storageRef.snapshot.ref.getDownloadURL().then(async url => {
          console.log(url);
          this.facility.facilityLogoUrl = url;
          this.facility.facilityLogoColor = await getDominantColor(url)
          this.updateFacility();
        });
      }
    );
  }

  getExtension(filename) {

    const parts = filename.split('.')
    const ext = parts[parts.length - 1]

    return ext
  }

  addTile(): void {
    if (this.facility.skinSettings.tiles == null)
      this.facility.skinSettings.tiles = [];

    this.facility.skinSettings.tiles.push({
      appName: "",
      packageName: "",
      icon: "",
      text: "",
      id: uuidv4(),
      type: "app"
    });
  }

  removeTile(tile): void {
    const index = this.facility.skinSettings.tiles.indexOf(tile);
    if (index >= 0) this.facility.skinSettings.tiles.splice(index, 1);
  }

  getSkinImage(skin): string {
    if (skin == "simplex")
      return require("../../../../assets/skins/simplexskin.png");
    if (skin == "default")
      return require("../../../../assets/skins/defaultskin.png");
  }

  async addMediaApp() {
    await firebase
      .firestore()
      .collection("facility")
      .doc(this.facility.id)
      .update({
        mediaApps: firebase.firestore.FieldValue.arrayUnion({
          id: uuidv4(),
          appName: this.appName,
          packageName: this.packageName,
          status: true
        })
      })
      .then(async () => {
        this.packageName = "";
        this.appName = "";
        const facility = await this.$store.dispatch("getFacility");
        this.updateMediaAppsToResident(facility.mediaApps);
      })
      .catch(err => {
        console.error(err);
      });
  }

  async addTvApp() {
    await firebase
      .firestore()
      .collection("facility")
      .doc(this.facility.id)
      .update({
        tvApps: firebase.firestore.FieldValue.arrayUnion({
          id: uuidv4(),
          tvAppName: this.tvAppName,
          tvPackageName: this.tvPackageName,
          status: this.tvAppStatus
        })
      })
      .then(async () => {
        this.tvAppName = "";
        this.tvPackageName = "";
        this.tvAppStatus = false;
        await this.$store.dispatch("getFacility");
      })
      .catch(err => {
        console.error(err);
      });
  }

  async updateMediaApp(mediaApp) {

    this.isMediaAppUpdating = true

    let facilityMediaApps = this.facility.mediaApps || []

    facilityMediaApps = facilityMediaApps.filter(x => x.id !== mediaApp.id)

    facilityMediaApps.push(mediaApp)

    if (mediaApp) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          mediaApps: facilityMediaApps
        })
        .then(async () => {
          this.packageName = "";
          this.appName = "";
          const facility = await this.$store.dispatch("getFacility");
          await this.updateMediaAppsToResident(facility.mediaApps);
          this.isMediaAppUpdating = false
          this.mediaAppSelectedIndex = -1
        })
        .catch(err => {
          this.isMediaAppUpdating = false
          this.mediaAppSelectedIndex = -1
          console.error(err);
        })
    }
  }

  async updateTvApp(tvApp) {

    let facilityTvApps = this.facility.tvApps || []

    facilityTvApps = facilityTvApps.filter(x => x.id !== tvApp.id)

    facilityTvApps.push(tvApp)

    if (tvApp) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          tvApps: facilityTvApps
        })
        .then(async () => {
          this.tvAppSelectedIndex = -1
          this.tvPackageName = "";
          this.tvAppName = "";
          await this.$store.dispatch("getFacility");
        })
        .catch(err => {
          console.error(err);
        });
    }
  }

  async deleteTvApp(tvApp) {

    if (tvApp) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          tvApps: firebase.firestore.FieldValue.arrayRemove(tvApp)
        })
        .then(async () => {
          this.tvPackageName = "";
          this.tvAppName = "";
          await this.$store.dispatch("getFacility");
        })
        .catch(err => {
          console.error(err);
        });
    }
  }

  async deleteMediaApp(mediaApp) {
    if (mediaApp) {
      this.isMediaAppDeleting = true
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          mediaApps: firebase.firestore.FieldValue.arrayRemove(mediaApp)
        })
        .then(async () => {
          this.packageName = "";
          this.appName = "";
          const facility = await this.$store.dispatch("getFacility");
          await this.updateMediaAppsToResident(facility.mediaApps);
          this.isMediaAppDeleting = false
          this.mediaAppSelectedIndex = - 1
        })
        .catch(err => {
          this.isMediaAppDeleting = false
          this.mediaAppSelectedIndex = - 1
          console.error(err);
        });
    }
  }

  async updateMediaAppsToResident(mediaApps) {
    const itemsToUpdate = []
    this.residents.forEach((doc) => {

      const updates = { mediaAppOverrides: [] }

      if (!doc.mediaAppOverrides) {
        updates.mediaAppOverrides = this.facility.mediaApps;
      }

      mediaApps.forEach(media => {
        if (!updates.mediaAppOverrides.find(mediaResident => mediaResident.id === media.id)) {
          updates.mediaAppOverrides.push(media);
        }
      });

      updates.mediaAppOverrides.forEach(mediaResident => {
        if (!mediaApps.find(media => media.id === mediaResident.id)) {
          const residentMediasResult = updates.mediaAppOverrides.filter(
            item => item.id != mediaResident.id
          );
          updates.mediaAppOverrides = residentMediasResult;
        }
      });

      itemsToUpdate.push({ docId: doc.id, updates: updates })
    });


    return await batchUpdate('residents', itemsToUpdate)
  }

  async updateResidentsAccessesOnMyhub() {
    if (this.residentFeaturesChanged.isServicesChanged
      || this.residentFeaturesChanged.isCommunityChanged
      || this.residentFeaturesChanged.isNoticeboardChanged) {

      const itemsToUpdate: any[] = []
      this.residents.forEach(resident => {
        if (resident?.accessesOnMyhub) {
          resident?.accessesOnMyhub.forEach(x => {
            if (x.name === this.residentAccessesOnMyhub.PremiumServices || x.name === this.residentAccessesOnMyhub.LifestyleEvents) {
              x.isEnabled = this.facility.additionalServicesEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Community) {
              x.isEnabled = this.facility.communityEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Media) {
              x.isEnabled = this.facility.mediaEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Maintenance) {
              x.isEnabled = this.facility.maintenanceEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Noticeboard) {
              x.isEnabled = this.facility.noticeboardEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Budget) {
              x.isEnabled = this.facility.myBudgetEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Calendar) {
              x.isEnabled = this.facility.calendarEnabled;
            }
          })
          itemsToUpdate.push({ docId: resident.id, updates: { accessesOnMyhub: resident?.accessesOnMyhub } })
        }
      })

      await batchUpdate('residents', itemsToUpdate).then(() => {
        console.log(`updated (${itemsToUpdate.length})/${this.residents.length} residents`)
      });
    }
  }

  onIsEnableADTChange(value) {
    if(value === true){
      this.facility.isEnableCSVImportForResidents = false;
      this.facility.isEnableManuallyAddDeleteResidents = false;
    }
  }

  onChangeIsStaffHubCalendar(value) {
    if (!value) {
      this.facility.isStaffHubPrintableCalendar = false
    }
  }

  async updateFamilyMembersCareSettings() {
    if(
      this.familyFeaturesChanged.isCareSummaryChanged ||
      this.familyFeaturesChanged.isCarePlanChanged ||
      this.familyFeaturesChanged.isDailyCareChanged ||
      this.familyFeaturesChanged.isDailyPlanChanged
    ) {
      const itemsToUpdate: any[] = []

      this.familyMembers.forEach(familyMember => {
        const familyId = familyMember.id;
        const updates: any = {};
        let isValid = false;

        const updateAccesses = (accesses) => {
          const changes = this.familyFeaturesChanged;
          const facility = this.facility;

          if (changes.isCareSummaryChanged) accesses.careSummary = facility.isCareSummary;
          if (changes.isCarePlanChanged) {
            accesses.carePlan = facility.isCarePlan;
            accesses.isEnabledCarePlan = facility.isCarePlan;
          }
          if (changes.isDailyPlanChanged) accesses.dailyPlan = facility.isDailyPlan;
          if (changes.isDailyCareChanged) accesses.dailyCare = facility.isDailyCare;
        };


        if (familyMember?.staffControlledFamilyAccesses) {
          updateAccesses(familyMember.staffControlledFamilyAccesses);
          isValid = true;
          updates.staffControlledFamilyAccesses = familyMember.staffControlledFamilyAccesses;
        }

        if (familyMember?.familyAccesses) {
          updateAccesses(familyMember.familyAccesses);
          isValid = true;
          updates.familyAccesses = familyMember.familyAccesses;
        }

        if(isValid) {
          itemsToUpdate.push({ docId: familyId, updates: updates})
        }
      })

      await batchUpdate('familyHubUsers', itemsToUpdate).then(() => {
        console.log(`updated (${itemsToUpdate.length})/${this.familyMembers.length} family members`)
      });
    }
  }

  @Watch("facility.name")
  onFacilityName() {
    this.isChangedFacilityName = true;
  }

  @Watch("facility.additionalServicesEnabled")
  onChangeAdditionalServicesEnabled() {
    this.residentFeaturesChanged.isServicesChanged = true;
  }

  @Watch("facility.communityEnabled")
  onChangeCommunityEnabled() {
    this.residentFeaturesChanged.isCommunityChanged = true;
  }

  @Watch("facility.mediaEnabled")
  onChangeMediaEnabled() {
    this.residentFeaturesChanged.isMediaChanged = true;
  }

  @Watch("facility.maintenanceEnabled")
  onChangeMaintenanceEnabled() {
    this.residentFeaturesChanged.isMaintenanceChanged = true;
  }

  @Watch("facility.noticeboardEnabled")
  onChangeNoticeEnabled() {
    this.residentFeaturesChanged.isNoticeboardChanged = true;
  }

  @Watch("facility.myBudgetEnabled")
  onChangeBudgetEnabled() {
    this.residentFeaturesChanged.isBudgetChanged = true;
  }

  @Watch("facility.calendarEnabled")
  onChangeCalendarEnabled() {
    this.residentFeaturesChanged.isCalendarChanged = true;
  }

  @Watch("facility.isCareSummary")
  onChangeCareSummary(value) {
    this.familyFeaturesChanged.isCareSummaryChanged = true;
  }

  @Watch("facility.isDailyPlan")
  onChangeDailyPlan(value) {
    this.familyFeaturesChanged.isDailyPlanChanged = true;
  }

  @Watch("facility.isCarePlan")
  onChangeCarePlan(value) {
    this.familyFeaturesChanged.isCarePlanChanged = true;
    this.facility.isEnabledCarePlan = value;
  }

  @Watch("facility.isDailyCare")
  onChangeDailyCare(value) {
    this.familyFeaturesChanged.isDailyCareChanged = true;
  }
}